import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";

const STORAGE_KEY = "intellegnium_cookie_consent_v1";

const defaultConsent = {
  essential: true,
  analytics: false,
  marketing: false,
  functional: false,
};

const CookieConsentContext = createContext(null);

function safeParse(value) {
  try {
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
}

function loadMetaPixel(pixelId) {
  if (!pixelId || typeof window === "undefined" || window.fbq) return;

  const fbq = (window.fbq = window.fbq || function () {
    fbq.callMethod
      ? fbq.callMethod.apply(fbq, arguments)
      : fbq.queue.push(arguments);
  });

  if (!window._fbq) window._fbq = fbq;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.queue = fbq.queue || [];

  const script = document.createElement("script");
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";

  const firstScript = document.getElementsByTagName("script")[0];
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }

  window.fbq("init", pixelId);
  window.fbq("track", "PageView");
}

export function CookieConsentProvider({ children, metaPixelId }) {
  const [consent, setConsent] = useState(defaultConsent);
  const [bannerOpen, setBannerOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    const stored = safeParse(localStorage.getItem(STORAGE_KEY));

    if (stored) {
      setConsent({ ...defaultConsent, ...stored, essential: true });
      setBannerOpen(false);
    } else {
      setBannerOpen(true);
    }
  }, []);

  useEffect(() => {
    if (consent.marketing) {
      loadMetaPixel(metaPixelId);
    }
  }, [consent.marketing, metaPixelId]);

  const persistConsent = useCallback((nextConsent) => {
    const merged = {
      ...defaultConsent,
      ...nextConsent,
      essential: true,
    };

    setConsent(merged);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
    setBannerOpen(false);
    setSettingsOpen(false);
  }, []);

  const acceptAll = useCallback(() => {
    persistConsent({
      essential: true,
      analytics: true,
      marketing: true,
      functional: true,
    });
  }, [persistConsent]);

  const rejectAll = useCallback(() => {
    persistConsent({
      essential: true,
      analytics: false,
      marketing: false,
      functional: false,
    });
  }, [persistConsent]);

  const savePreferences = useCallback(
    (prefs) => persistConsent(prefs),
    [persistConsent]
  );

  const value = useMemo(
    () => ({
      consent,
      bannerOpen,
      settingsOpen,
      setSettingsOpen,
      acceptAll,
      rejectAll,
      savePreferences,
    }),
    [consent, bannerOpen, settingsOpen, acceptAll, rejectAll, savePreferences]
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    throw new Error("useCookieConsent must be used within CookieConsentProvider");
  }
  return ctx;
}